import { FilterOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { useRouter } from '@tanstack/react-router'
import { Alert, Button, Input, Segmented, Select, Skeleton, Tag } from 'antd'
import { useUnresolvedThreadCounts } from 'features/libra/comments/commentsApi'
import { CreateTicketsForGroupModal } from 'features/libra/dashboard/CreateTicketsForGroupModal'
import { LibraDashboardConfig } from 'features/libra/dashboard/libraDashboardApi'
import { type LifecycleConfig } from 'features/libra/lifecycle/lifecycleTypes'
import {
  type LibraTicket,
  LibraTicketGroup,
  useLibraTickets,
  useLibraWorkflows,
} from 'features/libra/ticket/libraTicketsApi'
import {
  getLatestTicketPerAssetForStats,
  getLatestVersions,
} from 'features/libra/ticket/ticketVersioning'
import {
  ELIGIBILITY_STATUS,
  ELIGIBILITY_UI_LABELS,
  type EligibilityStatus,
} from 'features/libra/types/eligibility'
import { useGeckoPermissions } from 'features/permissions'
import { useRolodexEntries } from 'features/rolodex'
import uniq from 'lodash/uniq'
import type { FC } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { createLibraSearch } from 'routes/libra/-searchParams'
import styled from 'styled-components'
import { getLibraTicketGroupAdminPermission, RolodexEntryType } from 'types'

import { AssetListView } from './AssetListView'
import { AssetStatusTracker } from './AssetStatusTracker'
import { CreatorFilterButton } from './CreatorFilterButton'
import { ExecutiveDashboard } from './ExecutiveDashboard'
import { AssetStatus, getAssetStatus } from './getAssetStatus'
import { LifecycleBoardView } from './LifecycleBoardView'

type ViewMode = 'list' | 'board'
const VIEW_MODE_STORAGE_KEY = 'libra-dashboard-view-mode'

export const LibraDashboardContent: FC<{
  config: LibraDashboardConfig
  group: LibraTicketGroup
  unitId?: string
  orgId?: string
  dashboardConfigId?: string
  onRefreshGroup: () => void
}> = ({ config, group, onRefreshGroup }) => {
  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(
    undefined
  )
  const [selectedStatus, setSelectedStatus] = useState<AssetStatus | undefined>(
    undefined
  )

  const groupId = group.id
  const { hasPermissions } = useGeckoPermissions()

  const isAdmin = hasPermissions([getLibraTicketGroupAdminPermission(groupId)])
  const canCreateVersion = useCallback(() => isAdmin, [isAdmin])

  const {
    tickets,
    isLoading: ticketsLoading,
    forceRefresh,
  } = useLibraTickets(
    useMemo(
      () => ({
        [RolodexEntryType.LIBRA_TICKET_GROUP]: [groupId],
      }),
      [groupId]
    )
  )

  const organizationId = config.tags?.organization as string | undefined
  const latestTickets = useMemo(() => getLatestVersions(tickets), [tickets])

  const latestTicketIds = useMemo(
    () => latestTickets.map((t) => t.id),
    [latestTickets]
  )
  const { unresolvedCounts } = useUnresolvedThreadCounts(latestTicketIds)

  const workflowIds = useMemo(
    () =>
      uniq(
        latestTickets
          .map((ticket) => ticket.workflowId)
          .filter((id): id is string => id !== undefined)
      ),
    [latestTickets]
  )

  const { libraWorkflows } = useLibraWorkflows(
    useMemo(() => ({ ids: workflowIds }), [workflowIds])
  )

  const workflowMap = useMemo(
    () => new Map(libraWorkflows.map((w) => [w.id, w])),
    [libraWorkflows]
  )

  const lifecycleConfig = useMemo<LifecycleConfig | undefined>(() => {
    for (const w of libraWorkflows) {
      if (w.lifecycleConfig) return w.lifecycleConfig
    }
    return undefined
  }, [libraWorkflows])

  const [viewMode, setViewModeRaw] = useState<ViewMode>(() => {
    const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY)
    return stored === 'board' ? 'board' : 'list'
  })

  const setViewMode = useCallback((mode: ViewMode) => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode)
    setViewModeRaw(mode)
  }, [])
  const [filterText, setFilterText] = useState('')
  const [selectedEligibility, setSelectedEligibility] = useState<
    EligibilityStatus | undefined
  >(undefined)
  const [selectedCreators, setSelectedCreators] = useState<string[]>([])
  const [selectedStage, setSelectedStage] = useState<string | undefined>(
    undefined
  )

  // Filter tickets by selected site
  const filteredTickets = useMemo(() => {
    if (!selectedSiteId) return latestTickets
    return latestTickets.filter((ticket) => ticket.tags.site === selectedSiteId)
  }, [latestTickets, selectedSiteId])

  const filteredAllTickets = useMemo(() => {
    if (!selectedSiteId) return tickets
    return tickets.filter((ticket) => ticket.tags.site === selectedSiteId)
  }, [selectedSiteId, tickets])
  const statsTickets = useMemo(
    () => getLatestTicketPerAssetForStats(filteredAllTickets),
    [filteredAllTickets]
  )

  // Get unique site IDs
  const siteIds = useMemo(
    () =>
      uniq(
        latestTickets
          .map((ticket) => ticket.tags.site)
          .filter((site): site is string => site !== undefined)
      ),
    [latestTickets]
  )

  const isLoading = ticketsLoading

  // Get unique asset IDs from filtered tickets, optionally filtered by status
  const visibleAssetIds = useMemo(() => {
    const allAssetIds = uniq(
      statsTickets
        .map((ticket) => ticket.tags.unit)
        .filter((unit): unit is string => unit !== undefined)
    )

    if (!selectedStatus) return allAssetIds

    return allAssetIds.filter((unitId) => {
      const unitStatsTickets = statsTickets.filter(
        (ticket) => ticket.tags.unit === unitId
      )
      return getAssetStatus(unitStatsTickets, workflowMap) === selectedStatus
    })
  }, [selectedStatus, statsTickets, workflowMap])

  // Filter tickets to only show those for visible assets
  const visibleTickets = useMemo(() => {
    return filteredTickets.filter((ticket) =>
      visibleAssetIds.includes(ticket.tags.unit as string)
    )
  }, [filteredTickets, visibleAssetIds])
  const visibleAllTickets = useMemo(() => {
    return filteredAllTickets.filter((ticket) =>
      visibleAssetIds.includes(ticket.tags.unit as string)
    )
  }, [filteredAllTickets, visibleAssetIds])
  const visibleStatsTickets = useMemo(() => {
    return statsTickets.filter((ticket) =>
      visibleAssetIds.includes(ticket.tags.unit as string)
    )
  }, [statsTickets, visibleAssetIds])

  const creatorEmails = useMemo(() => {
    const emails = new Set<string>()
    for (const t of visibleTickets) {
      if (t.createdBy) emails.add(t.createdBy)
    }
    return [...emails].sort()
  }, [visibleTickets])

  const stageOptions = useMemo(() => {
    if (!lifecycleConfig) return []
    return lifecycleConfig.stages.map((stage) => ({
      value: stage.id,
      label: stage.name,
    }))
  }, [lifecycleConfig])

  const handleStageSelect = useCallback((value: string) => {
    setSelectedStage((prev) => (prev === value ? undefined : value))
  }, [])

  const handleStageClear = useCallback(() => setSelectedStage(undefined), [])

  const router = useRouter()
  const handleBoardTicketClick = useCallback(
    (ticket: LibraTicket) => {
      const orgId = ticket.tags.organization as string | undefined
      const workflowId = ticket.workflowId
      if (!orgId || !workflowId) return
      router.navigate({
        to: '/libra',
        search: createLibraSearch({
          orgId,
          workflowId,
          ticket: ticket.id,
          thread: undefined,
        }),
      })
    },
    [router]
  )

  if (!organizationId) {
    return (
      <ErrorMessage>
        <Alert
          type='info'
          message='Please select an Organization to view NII progress'
          showIcon
        />
      </ErrorMessage>
    )
  }

  return (
    <>
      <ContentWrapper>
        <TopRow>
          <SiteFilterRow
            siteIds={siteIds}
            selectedSiteId={selectedSiteId}
            setSelectedSiteId={setSelectedSiteId}
          />
          <ButtonContainer>
            <CreateTicketButton
              organizationId={organizationId}
              group={group}
              onSuccess={forceRefresh}
            />
          </ButtonContainer>
        </TopRow>

        <ExecutiveDashboard
          tickets={statsTickets}
          isLoading={isLoading}
          totalAssets={group.totalAssets}
          group={group}
          selectedSiteId={selectedSiteId}
          onRefreshGroup={onRefreshGroup}
          siteFilterSiteIds={siteIds}
        />
        <AssetStatusTracker
          tickets={statsTickets}
          selectedStatus={selectedStatus}
          onStatusSelect={setSelectedStatus}
        />
        <ViewToolbar>
          <ViewFilters>
            <FilterInput
              placeholder='Filter assets'
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              allowClear
              prefix={<FilterIcon />}
            />
            {viewMode === 'list' && (
              <CreatorFilterButton
                emails={creatorEmails}
                selectedEmails={selectedCreators}
                onChange={setSelectedCreators}
              />
            )}
            {viewMode === 'list' && lifecycleConfig && (
              <StageSelect
                placeholder='Stage'
                value={selectedStage}
                onSelect={handleStageSelect}
                onClear={handleStageClear}
                allowClear
                suffixIcon={<FilterIcon />}
                options={stageOptions}
              />
            )}
            <EligibilitySelect
              placeholder='NII Eligibility'
              value={selectedEligibility}
              onSelect={(value: EligibilityStatus) => {
                if (selectedEligibility === value) {
                  setSelectedEligibility(undefined)
                } else {
                  setSelectedEligibility(value)
                }
              }}
              onClear={() => setSelectedEligibility(undefined)}
              allowClear
              suffixIcon={<FilterIcon />}
              options={[
                {
                  value: ELIGIBILITY_STATUS.ELIGIBLE,
                  label: ELIGIBILITY_UI_LABELS[ELIGIBILITY_STATUS.ELIGIBLE],
                },
                {
                  value: ELIGIBILITY_STATUS.NOT_ELIGIBLE,
                  label: ELIGIBILITY_UI_LABELS[ELIGIBILITY_STATUS.NOT_ELIGIBLE],
                },
                {
                  value: ELIGIBILITY_STATUS.UNCLEAR,
                  label: ELIGIBILITY_UI_LABELS[ELIGIBILITY_STATUS.UNCLEAR],
                },
              ]}
            />
          </ViewFilters>
          {lifecycleConfig && (
            <ViewToggle
              value={viewMode}
              onChange={(val) => setViewMode(val as ViewMode)}
              options={[
                { label: 'List', value: 'list' },
                { label: 'Board', value: 'board' },
              ]}
            />
          )}
        </ViewToolbar>
        {viewMode === 'board' && lifecycleConfig ? (
          <LifecycleBoardView
            lifecycleConfig={lifecycleConfig}
            tickets={visibleTickets}
            allTickets={visibleAllTickets}
            workflowMap={workflowMap}
            onTicketClick={handleBoardTicketClick}
            onRefresh={forceRefresh}
            filterText={filterText}
            selectedEligibility={selectedEligibility}
            canCreateVersion={canCreateVersion}
            unresolvedCounts={unresolvedCounts}
          />
        ) : (
          <AssetListView
            tickets={visibleTickets}
            allTickets={visibleAllTickets}
            statsTickets={visibleStatsTickets}
            isLoading={isLoading}
            onRefresh={forceRefresh}
            filterText={filterText}
            selectedEligibility={selectedEligibility}
            selectedCreators={selectedCreators}
            selectedStage={selectedStage}
            lifecycleConfig={lifecycleConfig}
            canCreateVersion={canCreateVersion}
            unresolvedCounts={unresolvedCounts}
          />
        )}
      </ContentWrapper>
    </>
  )
}

const CreateTicketButton: FC<{
  organizationId: string | undefined
  group: LibraTicketGroup | undefined
  onSuccess: () => void
}> = ({ organizationId, group, onSuccess }) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const isDisabled = !organizationId || !group

  return (
    <>
      <Button
        type='primary'
        icon={<PlusCircleOutlined />}
        onClick={() => setIsCreateModalOpen(true)}
        disabled={isDisabled}>
        Create Tickets
      </Button>
      {organizationId && group && (
        <CreateTicketsForGroupModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={onSuccess}
          organizationId={organizationId}
          groupId={group.id}
          groupTitle={group.title}
        />
      )}
    </>
  )
}

const SiteFilterRow: FC<{
  siteIds: ReadonlyArray<string>
  selectedSiteId: string | undefined
  setSelectedSiteId: (siteId: string | undefined) => void
}> = ({ siteIds, selectedSiteId, setSelectedSiteId }) => {
  const { sites: siteEntries, isLoading: sitesLoading } = useRolodexEntries(
    useMemo(
      () => ({
        type: RolodexEntryType.SITE,
        ids: [...siteIds],
      }),
      [siteIds]
    )
  )

  const sortedSiteEntries = useMemo(
    () =>
      siteEntries.toSorted((a, b) =>
        (a.data.name ?? '').localeCompare(b.data.name ?? '')
      ),
    [siteEntries]
  )

  if (sitesLoading) {
    return (
      <SiteFiltersContainer>
        <Skeleton active paragraph={{ rows: 1 }} />
      </SiteFiltersContainer>
    )
  }

  if (siteEntries.length === 0) {
    return <SiteFiltersContainer />
  }

  return (
    <SiteFiltersContainer>
      <SiteFiltersLabel>Sites:</SiteFiltersLabel>
      <SitePills>
        <SiteTag
          $isSelected={!selectedSiteId}
          onClick={() => setSelectedSiteId(undefined)}>
          All Sites
        </SiteTag>
        {sortedSiteEntries.map((site) => (
          <SiteTag
            key={site.id}
            $isSelected={selectedSiteId === site.id}
            onClick={() =>
              setSelectedSiteId(
                selectedSiteId === site.id ? undefined : site.id
              )
            }>
            {site.data.name as string}
          </SiteTag>
        ))}
      </SitePills>
    </SiteFiltersContainer>
  )
}

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  min-height: 0;
`

const ErrorMessage = styled.div`
  padding: 1rem;
`

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
`

const SiteFiltersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
`

const ButtonContainer = styled.div`
  flex-shrink: 0;
`

const ViewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-shrink: 0;
  height: 32px;

  > * {
    height: 100%;
  }
`

const ViewFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  height: 100%;

  > * {
    height: 100%;
  }
`

const FilterInput = styled(Input)`
  flex: 1 1 0;
  min-width: 180px;
  display: flex;
  align-items: center;
`

const FilterIcon = styled(FilterOutlined)`
  color: ${({ theme }) => theme.colors.gray[700]};
`

const StageSelect = styled(Select)`
  flex-shrink: 0;
  width: 180px;
` as typeof Select

const EligibilitySelect = styled(Select)`
  flex-shrink: 0;
  width: 200px;
` as typeof Select

const ViewToggle = styled(Segmented)`
  flex-shrink: 0;
`

const SiteFiltersLabel = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 0.875rem;
  white-space: nowrap;
`

const SitePills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const SiteTag = styled(Tag)<{ $isSelected: boolean }>`
  cursor: pointer;
  margin: 0;
  padding: 4px 12px;
  border-radius: 16px;
  background: ${({ theme, $isSelected }) =>
    $isSelected ? theme.forecast.primary : theme.colors.gray[700]};
  border-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.forecast.primary : theme.colors.gray[700]};
  color: ${({ theme, $isSelected }) =>
    $isSelected ? '#fff' : theme.forecast.activeText};

  &:hover {
    background: ${({ theme, $isSelected }) =>
      $isSelected ? theme.forecast.primary : theme.colors.gray[600]};
    border-color: ${({ theme, $isSelected }) =>
      $isSelected ? theme.forecast.primary : theme.colors.gray[600]};
    color: ${({ theme, $isSelected }) =>
      $isSelected ? '#fff' : theme.forecast.activeText};
  }
`
